import { useTranslation } from 'react-i18next';
import _uniq from 'lodash/uniq';

import { regexp } from 'constants/regexp';

import { Rule, RuleObject } from 'antd/es/form';
import isValueEmpty from 'utils/common/isValueEmpty';
import convertPercentToFraction from 'utils/math/convertPercentToFraction';
import convertFractionToPercent from 'utils/math/convertFractionToPercent';

interface IValidatorHook {
  required: Rule;
  requiredNoMessage: Rule;
  email: Rule;
  digitsOnly: Rule;
  hasUpperCase: Rule;
  hasLowerCase: Rule;
  hasNumber: Rule;
  hasSpecialChar: Rule;
  fractionalNumber: Rule;

  ga: Rule[];
  password: Rule[];
  beneficiaries: Rule[];

  passwordWithMatch: (match?: string) => Rule[];
  requiredLen: (len: number, message: string) => Rule;
  requiredMinLen: (min: number, message?: string) => Rule;
  requiredMaxLen: (max: number, message?: string) => Rule;
  requiredMinNum: (min: number, options?: { equal?: boolean }) => Rule;
  requiredKey: (key: string) => Rule;
  totalFeeMax: (poolFee: number, maxTotalFee: number) => Rule;
}

enum ErrorMessage {
  REQUIRED = 'validation.required',
  EMAIL_NOT_VALID = 'validation.email.notValid',
  DIGITS_ONLY = 'validation.digitsOnly',
  GA_LENGTH = 'validation.ga.length',
  HAS_UPPER_CASE = 'validation.hasUpperCase',
  HAS_LOWER_CASE = 'validation.hasLowerCase',
  HAS_NUMBER = 'validation.hasNumber',
  HAS_SPECIAL_CHAR = 'validation.hasSpecialChar',
  FRACTIONAL_NUMBERS = 'validation.franctionalNumbers',
  DUBLICATE_ACCOUNTS = 'validation.duplicateAccounts',
  MIN_NUMBER = 'validation.minNumber',
  MIN_NUMBER_EQUAL = 'validation.minNumberEqual',
  MIN_LENGHT = 'validation.minLenght',
  MAX_LENGHT = 'validation.maxLenght',
  TOTAL_FEE_EXCEED = 'validation.totalFeeExceeds',
}

export default function useValidation(): IValidatorHook {
  const { t } = useTranslation();

  const required: Rule = { required: true, message: t(ErrorMessage.REQUIRED) };
  const requiredNoMessage: Rule = { required: true, message: '' };
  const email: Rule = { type: 'email', message: t(ErrorMessage.EMAIL_NOT_VALID) };
  const digitsOnly: Rule = { pattern: regexp.digitsOnly, message: t(ErrorMessage.DIGITS_ONLY) };
  const hasUpperCase: Rule = { pattern: regexp.upperCase, message: t(ErrorMessage.HAS_UPPER_CASE) };
  const hasLowerCase: Rule = { pattern: regexp.lowerCase, message: t(ErrorMessage.HAS_LOWER_CASE) };
  const hasNumber: Rule = { pattern: regexp.number, message: t(ErrorMessage.HAS_NUMBER) };
  const hasSpecialChar: Rule = { pattern: regexp.specialChar, message: t(ErrorMessage.HAS_SPECIAL_CHAR) };
  const fractionalNumber: Rule = { pattern: regexp.fractionalNumber, message: t(ErrorMessage.FRACTIONAL_NUMBERS) };

  const getLenRule = (len: number, message: string): Rule => {
    return { len, message: t(message) };
  };

  const getMinLenRule = (min: number, message?: string): Rule => {
    return { min, message: t(message || ErrorMessage.MIN_LENGHT, { min }) };
  };

  const getMaxLenRule = (max: number, message?: string): Rule => {
    return { max, message: t(message || ErrorMessage.MAX_LENGHT, { max }) };
  };

  const getMinNumRule = (min: number, options?: { equal?: boolean }): Rule => {
    const validatorRule: RuleObject = {
      validator: (_, value) => {
        const { equal = true } = options || {};
        const isNumber = !isNaN(+value);

        const errorMessage = equal ? t(ErrorMessage.MIN_NUMBER_EQUAL, { min }) : t(ErrorMessage.MIN_NUMBER, { min });

        if (isNumber) {
          const isValid = equal ? +value >= min : +value > min;
          return isValid ? Promise.resolve() : Promise.reject(new Error(errorMessage));
        }

        return Promise.reject(new Error(errorMessage));
      },
    };

    return validatorRule;
  };

  const getRequiredKeyRule = (key: string): Rule => {
    return {
      validator: (_, value) => {
        if (isValueEmpty(value?.[key])) {
          return Promise.reject(new Error(t(ErrorMessage.REQUIRED)));
        }

        return Promise.resolve();
      },
    };
  };

  const getTotalFeeMaxRule = (poolFee: number, maxTotalFee: number): Rule => {
    const validatorRule: RuleObject = {
      validator: (_, value) => {
        if (value === null || value === undefined) {
          return Promise.resolve();
        }

        const accountFeeFraction = convertPercentToFraction(value);
        const calculatedTotal = poolFee + accountFeeFraction;

        if (calculatedTotal > maxTotalFee) {
          return Promise.reject(
            new Error(t(ErrorMessage.TOTAL_FEE_EXCEED, { max: convertFractionToPercent(maxTotalFee, { symbol: '' }) })),
          );
        }

        return Promise.resolve();
      },
    };

    return validatorRule;
  };

  const getGoogleAuthenticatorValidator = (): Rule[] => {
    const length = getLenRule(6, ErrorMessage.GA_LENGTH);

    return [required, digitsOnly, length];
  };

  const getPasswordValidationRules = (passwordMatch?: string): Rule[] => {
    const validatorRule: RuleObject = {
      validator: (_, value) => {
        let passwordsMatched = true;

        if (passwordMatch) {
          passwordsMatched = passwordMatch === value;
        }

        return passwordsMatched ? Promise.resolve() : Promise.reject(new Error(''));
      },
    };

    const minLen = getMinLenRule(12, '');
    const maxLen = getMaxLenRule(32, '');

    return [required, minLen, maxLen, hasUpperCase, hasLowerCase, hasNumber, hasSpecialChar, validatorRule];
  };

  const getBeneficiariesValidationRules = (): Rule[] => {
    const validatorRule: RuleObject = {
      validator: (_, items) => {
        if (!items) {
          return Promise.resolve();
        }

        const filtredItems = items.filter(item => item.id).map(item => item.id);
        const uniqueCount = _uniq(filtredItems).length;
        const totalCount = filtredItems.length;

        if (uniqueCount === totalCount) {
          return Promise.resolve();
        }

        return Promise.reject(new Error(t(ErrorMessage.DUBLICATE_ACCOUNTS)));
      },
    };

    return [validatorRule];
  };

  return {
    required,
    requiredNoMessage,
    email,
    digitsOnly,
    hasUpperCase,
    hasLowerCase,
    hasNumber,
    hasSpecialChar,
    fractionalNumber,
    beneficiaries: getBeneficiariesValidationRules(),
    ga: getGoogleAuthenticatorValidator(),
    password: getPasswordValidationRules(),
    passwordWithMatch: getPasswordValidationRules,
    requiredLen: getLenRule,
    requiredMinLen: getMinLenRule,
    requiredMaxLen: getMaxLenRule,
    requiredMinNum: getMinNumRule,
    requiredKey: getRequiredKeyRule,
    totalFeeMax: getTotalFeeMaxRule,
  };
}
